<template>
  <div class="menu-container">
    <div class="logo-text">
      <img class="logo" src="@/assets/images/menu/menu-logo.png" alt="" srcset="" />
      <span class="text">牛姆管理后台</span>
    </div>
    <el-menu :default-active="activeMenuPath" class="side-menu">
      <template v-for="item in menuRoutes">
        <el-menu v-if="item.children && item.children.length > 0 && !item.hidden" :collapse="true"
          :default-active="activeMenuPath" :popper-offset="2" router>
          <el-sub-menu popper-class="side-sub-menu" :index="item.path" :teleported="false">
            <template #title>
              <div class="icon-text">
                <Icon class="icon" :name="getSubIconName(item.meta?.icon || '', item.children)" size="22px" />
                {{ item.meta?.title || '' }}
              </div>
            </template>
            <!-- 二级 -->
            <div class="item-group">{{ item.meta?.title || '' }}</div>
            <router-link v-for="child in item.children" :key="child.path" :to="child.path">
              <el-menu-item :index="child.path">
                <div class="icon-text">
                  {{ child.meta?.title || '' }}
                </div>
              </el-menu-item>
            </router-link>
          </el-sub-menu>
        </el-menu>
        <router-link v-else-if="!item.hidden" :to="item.path">
          <el-menu-item :index="item.path">
            <div class="icon-text">
              <Icon class="icon" :name="`${item.meta?.icon || ''}${isMenuActive(item.path) ? '-fill' : ''}`"
                size="22px" />
              {{ item.meta?.title || '' }}
            </div>
          </el-menu-item>
        </router-link>
      </template>
    </el-menu>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import Icon from '@/components/Icons.vue'
import { useRouteStore } from '@/stores'
import { constantRoutes } from '@/router'

/**
 * 当前路由对象
 */
const route = useRoute()

/**
 * 路由状态管理实例
 */
const routeStore = useRouteStore()

/**
 * 本地路由数据，用于存储从后端获取的动态路由
 * @type {ref<Array>}
 */
const localRoutes = ref([])

/**
 * 默认菜单路由（从静态路由中获取）
 * 当后端未返回路由时使用此默认值
 * @type {computed<Array>}
 */
const defaultMenuRoutes = computed(() => {
  const rootRoute = constantRoutes.find((item) => item.path === '')
  return rootRoute?.children || []
})

/**
 * 监听路由加载状态变化
 * 当路由从后端加载完成后，更新本地路由数据
 * immediate: true 确保组件挂载时也会检查一次
 */
watch(
  () => routeStore.isRoutesLoaded,
  (loaded) => {
    if (loaded && routeStore.routes.length > 0) {
      localRoutes.value = routeStore.routes
      console.log('菜单路由已更新:', routeStore.routes)
    }
  },
  { immediate: true },
)

/**
 * 过滤菜单路由
 * 移除隐藏的路由，并递归处理子路由
 * @param {Array} routes - 路由数组
 * @returns {Array} 过滤后的路由数组
 */
const filterMenuRoutes = (routes = []) => {
  return routes
    .filter((routeItem) => routeItem?.hidden !== true && routeItem?.show !== false)
    .map((routeItem) => {
      const nextRoute = { ...routeItem }

      if (Array.isArray(routeItem.children)) {
        nextRoute.children = filterMenuRoutes(routeItem.children)
      }

      return nextRoute
    })
    .filter(
      (routeItem) =>
        !Array.isArray(routeItem.children) ||
        routeItem.children.length > 0 ||
        routeItem.meta?.isMenu,
    )
}

/**
 * 最终菜单路由数据
 * 优先使用后端返回的动态路由，若无则使用静态路由
 * @type {computed<Array>}
 */
const menuRoutes = computed(() => {
  const routes = localRoutes.value.length > 0 ? localRoutes.value : defaultMenuRoutes.value
  return filterMenuRoutes([...routes])
})

/**
 * 获取当前激活的菜单路径
 * 通过遍历菜单路由，找到与当前路由匹配的菜单项
 * @returns {string} 激活菜单的路径
 */
const getActiveMenuPath = () => {
  const currentPath = route.path
  for (const item of menuRoutes.value) {
    if (item.children) {
      for (const child of item.children) {
        if (currentPath.startsWith(child.path)) {
          return child.path
        }
      }
    }
    if (currentPath.startsWith(item.path)) {
      return item.path
    }
  }
  return currentPath
}

/**
 * 当前激活的菜单路径（计算属性）
 * @type {computed<string>}
 */
const activeMenuPath = computed(() => getActiveMenuPath())

/**
 * 判断指定菜单路径是否处于激活状态
 * @param {string} menuPath - 菜单路径
 * @returns {boolean} 是否激活
 */
const isMenuActive = (menuPath) => {
  return route.path.startsWith(menuPath)
}

/**
 * 获取子菜单图标的名称
 * 根据子菜单是否有激活状态，决定是否添加 -fill 后缀
 * @param {string} icon - 图标名称
 * @param {Array} children - 子菜单数组
 * @returns {string} 图标名称（可能带 -fill 后缀）
 */
const getSubIconName = (icon = '', children) => {
  const isFill = children.some((item) => route.path.startsWith(item.path))
  return `${icon}${isFill ? '-fill' : ''}`
}

/**
 * 组件挂载时初始化
 * 如果路由已加载，同步本地路由数据
 */
onMounted(() => {
  if (routeStore.isRoutesLoaded && routeStore.routes.length > 0) {
    localRoutes.value = routeStore.routes
  }
})
</script>

<style lang="scss" scoped>
.menu-container {
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0px 6px 58px #c4cbd6;
  width: 200px;
  padding-bottom: 10px;
  flex-shrink: 0;

  .logo-text {
    padding: 0 17px;
    display: flex;
    align-items: center;
    margin-top: 34px;
    margin-bottom: 18px;

    .logo {
      width: 31px;
      height: 32px;
    }

    .text {
      margin-left: 12px;
      font-size: 20px;
      font-weight: bold;
      color: #000000;
    }
  }

  .el-menu {
    width: 100%;

    .icon {
      margin-right: 16px;
    }

    .el-menu-item,
    :deep(.el-sub-menu__title) {
      padding: 0 20px;
      font-size: 16px;
      color: #718096;
      line-height: inherit;
      height: auto;
      margin-bottom: 4px;

      &:hover {
        background-color: #fff !important;
        color: #3f8cff;
      }

      &.is-active {
        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 4px;
          height: 100%;
          border-radius: 2px;
          background: #3f8cff;
        }

        .icon-text {
          color: #3f8cff;
          border-radius: 8px;
          background: rgba(63, 140, 255, 0.1);
        }
      }
    }
  }

  .icon-text {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 12px;
    font-size: 14px;
    color: #718096;
  }
}

.side-menu {
  .el-sub-menu {
    &.is-active {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 4px;
        height: 100%;
        border-radius: 2px;
        background: #3f8cff;
      }

      .icon-text {
        color: #3f8cff;
        border-radius: 8px;
        background: rgba(63, 140, 255, 0.1);
      }
    }
  }

  :deep(.side-sub-menu) {
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0px 2px 6px rgba(42, 130, 228, 0.14);

    .el-menu {
      box-shadow: none;
      background: transparent;

      .item-group {
        height: 36px;
        font-size: 12px;
        color: #9c9c9c;
        line-height: 36px;
        border-bottom: 1px solid #eeeff2;
        padding: 0 12px;
      }

      a:focus-visible {
        outline: unset;
      }

      .el-menu-item {
        padding: 0 6px;
        margin-top: 4px;

        .icon-text {
          background-color: transparent;
          color: #718096;
          padding: 10px 8px;

          &:hover {
            background-color: #fff;
            color: #3f8cff;
          }
        }

        &.is-active {
          &::after {
            content: none;
          }

          .icon-text {
            color: #3f8cff;
            border-radius: 8px;
            background: rgba(63, 140, 255, 0.1);
          }
        }
      }
    }
  }
}
</style>
